import { useCallback, useMemo } from 'react';

import { useSendVerificationCode } from '@/api/gen/phone-verification/phone-verification.gen';
import { isZepQuizApiError } from '@/shared/error';

import { SEND_ERROR_MESSAGES } from '../constants/errorMessages';
import { DEFAULT_COUNTRY_CODE, toE164Format } from '../utils/countryCodeData';

interface SendVerificationCodeResponse {
  status: string;
  data: {
    remainingAttempts: number;
  };
}

interface UseSendSmsCodeOptions {
  onSuccess?: () => void;
  onError: (error: unknown) => void;
}

/**
 * SMS 인증번호 발송 훅
 * - E.164 형식으로 변환하여 API 호출
 * - 일일 5회 제한
 */
export const useSendSmsCode = (options: UseSendSmsCodeOptions) => {
  const mutation = useSendVerificationCode({
    mutation: {
      onSuccess: () => {
        options?.onSuccess?.();
      },
      onError: error => {
        options.onError(error);
      },
    },
  });

  const sendCode = useCallback(
    (phoneNumber: string) => {
      const formattedPhoneNumber = toE164Format(
        phoneNumber,
        DEFAULT_COUNTRY_CODE,
      );
      mutation.mutate({
        data: { phoneNumber: formattedPhoneNumber },
      });
    },
    [mutation],
  );

  const reset = useCallback(() => {
    mutation.reset();
  }, [mutation]);

  const error = useMemo(() => {
    if (isZepQuizApiError(mutation.error)) {
      if (mutation.error.status === 'PHONE_RESEND_COOLDOWN_NOT_EXPIRED') {
        return null; // 쿨다운 에러는 useCooldownTimer에서 처리
      }
      return SEND_ERROR_MESSAGES[mutation.error.status];
    }
    return null;
  }, [mutation.error]);

  const remainingAttempts = useMemo(() => {
    const response = mutation.data as SendVerificationCodeResponse | undefined;
    return response?.data?.remainingAttempts;
  }, [mutation.data]);

  return {
    sendCode,
    reset,
    isLoading: mutation.isPending,
    isSuccess: mutation.isSuccess,
    error,
    remainingAttempts,
  };
};
